import React from 'react';
import { ShieldCheck, Lock, Handshake, Scales, CheckCircle, Lightning } from '@phosphor-icons/react';

/**
 * "Payment Protection" landing section — explains escrow + dispute resolution.
 * Strict Tactical Brutalism: 4px borders, hard offset shadows, no soft glows.
 */
const PaymentProtection = () => {
  const steps = [
    {
      Icon: Lock,
      title: '1. FUND ESCROW',
      body: 'Homeowners fund a milestone before work starts. Money is held safely — neither side has full control until the milestone is approved.',
      color: '#D95800',
    },
    {
      Icon: Lightning,
      title: '2. WORK GETS DONE',
      body: 'The pro completes the milestone, uploads photos, and submits for approval. Everything is timestamped and tied to the job record.',
      color: '#2B4C6F',
    },
    {
      Icon: CheckCircle,
      title: '3. APPROVE & RELEASE',
      body: 'Homeowner reviews the work and releases funds with one tap. The contractor or worker gets paid the same day via Stripe Connect.',
      color: '#10B981',
    },
    {
      Icon: Scales,
      title: '4. DISPUTE? WE MEDIATE',
      body: 'If something goes wrong, the funds stay locked. ConstructHelp mediates and a fair resolution is enforced — no chargebacks, no fights.',
      color: '#EAB308',
    },
  ];

  return (
    <section
      className="py-16 border-y-4 border-black bg-[#E8E9EB] dark:bg-[#121212]"
      data-testid="payment-protection-section"
    >
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-2 border-black rounded mb-4 shadow-[3px_3px_0px_#000]">
            <ShieldCheck size={14} weight="fill" /> Payment Protection
          </div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight mb-3">
            Your money is safe.
            <span className="block text-[#D95800]">Period.</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-700 dark:text-neutral-300 max-w-2xl mx-auto">
            Every dollar that moves through ConstructHelp is held in escrow until both
            sides agree the work is done. If a dispute happens, we step in — your money
            stays locked the whole time.
          </p>
        </div>

        {/* 4-step strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10" data-testid="payment-protection-steps">
          {steps.map(({ Icon, title, body, color }) => (
            <div
              key={title}
              className="bg-white dark:bg-[#0F172A] border-4 border-black p-5 shadow-[6px_6px_0px_#000] hover:shadow-[8px_8px_0px_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all"
              style={{ borderTopColor: color, borderTopWidth: 8 }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center border-2 border-black mb-3"
                style={{ backgroundColor: color }}
              >
                <Icon size={24} weight="fill" className="text-white" />
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-1">
                {title}
              </div>
              <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        {/* Trust footer strip */}
        <div className="bg-black text-white border-4 border-black p-5 md:p-6 shadow-[6px_6px_0px_#D95800]">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-14 h-14 bg-[#D95800] flex items-center justify-center border-2 border-white flex-shrink-0">
                <Handshake size={28} weight="fill" className="text-black" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#D95800]">
                  Built-in trust
                </div>
                <div className="text-lg md:text-xl font-black uppercase leading-tight">
                  Escrow · E-Signed Contracts · ID Verification
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 md:gap-4 text-center">
              <div data-testid="trust-stat-escrow">
                <div className="text-2xl md:text-3xl font-black text-[#D95800] tabular-nums">100%</div>
                <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                  Escrowed
                </div>
              </div>
              <div data-testid="trust-stat-fee">
                <div className="text-2xl md:text-3xl font-black text-[#D95800] tabular-nums">0%</div>
                <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                  Platform Fee
                </div>
              </div>
              <div data-testid="trust-stat-payout">
                <div className="text-2xl md:text-3xl font-black text-[#D95800] tabular-nums">$0</div>
                <div className="text-[9px] font-bold uppercase tracking-widest text-neutral-400">
                  Payout Fees
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PaymentProtection;
